import { useMemo, useState } from "react";
import Box from "@/ui/atoms/Box";
import type { BoxProps } from "@/ui/atoms/Box";
import { retrieveExtractionAnalyticsNodes } from "./nodes/extractionAnalyticsNodes";
import DraggableGrid from "@/ui/organisms/DraggableGrid";
import type { TDraggableGridNode } from "@/ui/organisms/DraggableGrid";
import { retrieveDealAnalyticsNodes } from "./nodes/dealAnalyticsNodes";
import { retrieveFileAnalyticsNodes } from "./nodes/fileAnalyticsNodes";
import { retrieveUserAnalyticsNodes } from "./nodes/userAnalyticsNodes";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import Loader from "@/ui/atoms/Loader";
import ErrorMessage from "@/ui/atoms/ErrorMessage";
import type {
    DashboardAnalytics,
    DocEngineRequestDashboardRow,
    TDashboardFilter,
} from "@/ui/organisms/dashboardTypes";
import type { TDashboardTab } from "@/ui/router/routerPaths";
import { retrieveMappingAnalyticsNodes } from "./nodes/mappingAnalyticsNodes";
import TopBarOverview from "@/ui/molecules/TopBarOverview";
import Dropdown from "@/ui/molecules/Dropdown";
import type { UserPreferenceDashboardRow } from "@/ui/organisms/dashboardTypes";
import Grid from "@/ui/molecules/Grid";
import type { ColDef } from "@/ui/molecules/Grid";
import SearchBar from "@/ui/molecules/SearchBar";

export type ViewDashboardParams = {
    useState: () => {
        dashboardAnalytics: DashboardAnalytics | null;
        dashboardFilter: TDashboardFilter;
        dashboardAnalyticsLoadStatus:
            | "idle"
            | "pending"
            | "resolved"
            | "rejected";
        isProdaUser: boolean;
    };
    useCases: () => {
        setDashboardFilter: (value: TDashboardFilter) => void;
        handleBackButtonClicked: () => void;
    };
    getCurrentDate: () => Date;
};

export const dashboardTabs = [
    {
        value: "deal",
        label: "Project",
    },
    {
        value: "file",
        label: "File",
    },
    {
        value: "extraction",
        label: "Extraction",
    },
    {
        value: "user",
        label: "User",
    },
    {
        value: "mapping",
        label: "Mapping",
    },
    {
        value: "preference",
        label: "Parsing Preferences",
    },
    { value: "document-engine", label: "Document Engine Uploads" },
] as const;

const dashboardFilterOptions = [
    {
        value: "all",
        label: "All Users",
    },
    {
        value: "internal",
        label: "Internal Users",
    },
    {
        value: "external",
        label: "External Users",
    },
];

const isNotUndefinedTypeGuard = <T,>(
    value: T | undefined | null,
): value is T => {
    return value !== undefined && value !== null;
};

const CELL_HEIGHT = 15;

export const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const viewHeightToPixels = (vh: number): number => {
    if (typeof window !== "undefined") {
        return (vh * window.innerHeight) / 100;
    }
    return 0;
};

const formatPreferenceValue = (value: unknown): string => {
    if (value === null || value === undefined) return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "bigint") {
        return value.toString();
    }
    return JSON.stringify(value) ?? "—";
};

const preferenceColumnDefs: ColDef<UserPreferenceDashboardRow>[] = [
    { field: "userId", headerName: "User ID" },
    { field: "userEmail", headerName: "User email", minWidth: 240 },
    { field: "companyName", headerName: "Company", minWidth: 200 },
    {
        field: "userSelectedDatabases",
        headerName: "Selected databases",
    },
    { field: "model", headerName: "Model", minWidth: 240 },
    { field: "exportFormat", headerName: "Export format" },
    { field: "exportProdaIncludeMappings", headerName: "Export mappings" },
    { field: "exportProdaIncludeSummary", headerName: "Export summary" },
    {
        field: "exportProdaIncludeDetailedSummary",
        headerName: "Detailed summary",
    },
    { field: "exportIncludeNonOperating", headerName: "Non-operating" },
    { field: "userRegion", headerName: "Region" },
    { field: "isRentRollUploadEnabled", headerName: "Rent roll upload" },
    { field: "useModelForParsing", headerName: "Model parsing" },
    { field: "useDocumentEngine", headerName: "Document Engine" },
    { field: "modifiedAt", headerName: "Last modified", minWidth: 210 },
];

const preferenceDefaultColDef: ColDef<UserPreferenceDashboardRow> = {
    minWidth: 150,
    sortable: true,
    filter: true,
    resizable: true,
    valueFormatter: params => formatPreferenceValue(params.value),
};

const UserPreferencesTable = ({
    rows,
}: {
    rows: UserPreferenceDashboardRow[];
}) => {
    const [search, setSearch] = useState("");
    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return query
            ? rows.filter(row =>
                  Object.values(row).join("|").toLowerCase().includes(query),
              )
            : rows;
    }, [rows, search]);

    return (
        <Box
            sx={{
                height: "calc(100vh - 145px)",
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <SearchBar
                aria-label="Filter parsing preferences"
                placeholder="Filter parsing preferences"
                value={search}
                onChange={setSearch}
                sx={{ width: "320px", marginBottom: "1rem" }}
            />
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <Grid<UserPreferenceDashboardRow>
                    gridTheme="ag-theme-custom-proda"
                    rowData={filteredRows}
                    columnDefs={preferenceColumnDefs}
                    defaultColDef={preferenceDefaultColDef}
                    suppressCellFocus={true}
                    animateRows={true}
                />
            </Box>
        </Box>
    );
};

type DocEngineValidationRow = DocEngineRequestDashboardRow & {
    statementName: string | null;
    verdict: string | null;
    verdictReasons: string | null;
    rowTotalsFound: boolean | null;
    allRowTotalsMatch: boolean | null;
    columnTotalsFound: boolean | null;
    allColumnTotalsMatch: boolean | null;
};

const docEngineColumnDefs: ColDef<DocEngineValidationRow>[] = [
    { field: "requestedAt", headerName: "Request timestamp", minWidth: 230 },
    {
        field: "success",
        headerName: "Result",
        valueFormatter: params =>
            params.value === true ? "Success" : "Failure",
    },
    { field: "failureReason", headerName: "Failure reason", minWidth: 260 },
    { field: "statementName", headerName: "Statement", minWidth: 240 },
    { field: "rowTotalsFound", headerName: "Row totals found", minWidth: 180 },
    {
        field: "allRowTotalsMatch",
        headerName: "All row totals match",
        minWidth: 200,
    },
    {
        field: "columnTotalsFound",
        headerName: "Column totals found",
        minWidth: 200,
    },
    {
        field: "allColumnTotalsMatch",
        headerName: "All column totals match",
        minWidth: 230,
    },
    { field: "verdict", headerName: "Verdict" },
    {
        field: "verdictReasons",
        headerName: "Verdict reasons",
        minWidth: 300,
        autoHeight: true,
        wrapText: true,
    },
    { field: "extractionId", headerName: "Extraction ID" },
    { field: "userId", headerName: "User ID" },
    { field: "userEmail", headerName: "Email", minWidth: 240 },
    { field: "companyName", headerName: "Company", minWidth: 200 },
    { field: "databaseId", headerName: "Database ID" },
    { field: "projectId", headerName: "Project ID" },
    {
        field: "originalFilename",
        headerName: "Original filename",
        minWidth: 260,
    },
];

const DocEngineRequestsTable = ({
    rows,
}: {
    rows: DocEngineRequestDashboardRow[];
}) => {
    const [search, setSearch] = useState("");
    const validationRows = useMemo<DocEngineValidationRow[]>(
        () =>
            rows.flatMap(row => {
                const statements =
                    row.validationChecks.length > 0
                        ? row.validationChecks
                        : [{}];
                return statements.map(statement => {
                    const validation = statement["total_validation"];
                    const checks =
                        typeof validation === "object" && validation !== null
                            ? (validation as Record<string, unknown>)
                            : {};
                    const check = (key: string): boolean | null =>
                        typeof checks[key] === "boolean" ? checks[key] : null;
                    return {
                        ...row,
                        statementName:
                            typeof statement["name"] === "string"
                                ? statement["name"]
                                : null,
                        verdict:
                            typeof statement["verdict"] === "string"
                                ? statement["verdict"]
                                : null,
                        verdictReasons: Array.isArray(
                            statement["verdict_reasons"],
                        )
                            ? statement["verdict_reasons"]
                                  .filter(
                                      (reason): reason is string =>
                                          typeof reason === "string",
                                  )
                                  .join(", ")
                            : null,
                        rowTotalsFound: check("row_totals_found"),
                        allRowTotalsMatch: check("all_row_totals_match"),
                        columnTotalsFound: check("column_totals_found"),
                        allColumnTotalsMatch: check("all_column_totals_match"),
                    };
                });
            }),
        [rows],
    );
    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return query
            ? validationRows.filter(row =>
                  JSON.stringify(row).toLowerCase().includes(query),
              )
            : validationRows;
    }, [validationRows, search]);
    return (
        <Box
            sx={{
                height: "calc(100vh - 145px)",
                padding: "1.5rem",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <SearchBar
                aria-label="Filter Document Engine uploads"
                placeholder="Filter Document Engine uploads"
                value={search}
                onChange={setSearch}
                sx={{ width: "320px", marginBottom: "1rem" }}
            />
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <Grid<DocEngineValidationRow>
                    gridTheme="ag-theme-custom-proda"
                    rowData={filteredRows}
                    columnDefs={docEngineColumnDefs}
                    defaultColDef={{
                        minWidth: 150,
                        sortable: true,
                        filter: true,
                        resizable: true,
                        valueFormatter: params =>
                            formatPreferenceValue(params.value),
                    }}
                    suppressCellFocus={true}
                    animateRows={true}
                />
            </Box>
        </Box>
    );
};

export type ViewDashboardProps = {
    activeTab: TDashboardTab;
    setActiveTab: (value: TDashboardTab) => void;
} & Omit<BoxProps, "children">;

export const createViewDashboard = ({
    useState,
    useCases,
    getCurrentDate,
}: ViewDashboardParams) => {
    const ViewDashboard = ({ activeTab, setActiveTab }: ViewDashboardProps) => {
        const currentMonth = getCurrentDate().getMonth();
        const {
            dashboardAnalytics,
            dashboardFilter,
            dashboardAnalyticsLoadStatus,
            isProdaUser,
        } = useState();

        const { setDashboardFilter, handleBackButtonClicked } = useCases();

        const dealAnalyticsNodes =
            isNotUndefinedTypeGuard(dashboardAnalytics) &&
            isNotUndefinedTypeGuard(dashboardAnalytics.dealAnalytics) &&
            activeTab === "deal"
                ? retrieveDealAnalyticsNodes(
                      dashboardAnalytics.dealAnalytics,
                      currentMonth,
                  )
                : [];
        const fileUploadAnalyticsNodes =
            isNotUndefinedTypeGuard(dashboardAnalytics) &&
            isNotUndefinedTypeGuard(dashboardAnalytics.fileUploadAnalytics) &&
            activeTab === "file"
                ? retrieveFileAnalyticsNodes(
                      dashboardAnalytics.fileUploadAnalytics,
                      currentMonth,
                  )
                : [];

        const extractionAnalyticsNodes =
            isNotUndefinedTypeGuard(dashboardAnalytics) &&
            isNotUndefinedTypeGuard(dashboardAnalytics.extractionAnalytics) &&
            activeTab === "extraction"
                ? retrieveExtractionAnalyticsNodes(
                      dashboardAnalytics.extractionAnalytics,
                      currentMonth,
                  )
                : [];
        const userAnalyticsNodes =
            isNotUndefinedTypeGuard(dashboardAnalytics) &&
            isNotUndefinedTypeGuard(dashboardAnalytics.userAnalytics) &&
            activeTab === "user"
                ? retrieveUserAnalyticsNodes(
                      dashboardAnalytics.userAnalytics,
                      currentMonth,
                  )
                : [];
        const mappingAnalyticsNodes =
            isNotUndefinedTypeGuard(dashboardAnalytics) &&
            isNotUndefinedTypeGuard(dashboardAnalytics.mappingAnalytics) &&
            activeTab === "mapping"
                ? retrieveMappingAnalyticsNodes(
                      dashboardAnalytics.mappingAnalytics,
                  )
                : [];
        const nodes = [
            ...dealAnalyticsNodes,
            ...fileUploadAnalyticsNodes,
            ...extractionAnalyticsNodes,
            ...userAnalyticsNodes,
            ...mappingAnalyticsNodes,
        ] satisfies TDraggableGridNode[];

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <TopBarOverview
                    title="Dashboard"
                    backButtonLabel="Home"
                    onBackButtonClick={handleBackButtonClicked}
                    sx={{
                        marginBottom: "10px",
                    }}
                />
                {isProdaUser && dashboardAnalyticsLoadStatus !== "rejected" ? (
                    <>
                        <Box
                            sx={{
                                borderBottom:
                                    "1px solid var(--proda-white-130)",
                                height: "50px",
                                display: "flex",
                                gap: "1.5rem",
                                paddingLeft: "1.5rem",
                            }}
                        >
                            {dashboardTabs.map(({ value, label }) => (
                                <Box
                                    key={value}
                                    component="button"
                                    onClick={() => setActiveTab(value)}
                                    sx={{
                                        p: "0.8rem 0",
                                        border: "none",
                                        borderBottom: "2px solid",
                                        borderColor:
                                            activeTab === value
                                                ? "var(--primary-color)"
                                                : "transparent",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                        color:
                                            activeTab === value
                                                ? "var(--primary-color)"
                                                : "inherit",
                                        fontWeight:
                                            activeTab === value
                                                ? "bold"
                                                : "normal",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            color: "var(--primary-color)",
                                        },
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                            {activeTab !== "preference" && (
                                <Dropdown
                                    options={dashboardFilterOptions}
                                    label=""
                                    value={dashboardFilter}
                                    onChange={e => {
                                        setDashboardFilter(
                                            e as
                                                | "internal"
                                                | "external"
                                                | "all",
                                        );
                                    }}
                                    sx={{ width: "100px" }}
                                />
                            )}
                        </Box>
                        {dashboardAnalyticsLoadStatus === "pending" ? (
                            <LayoutFillParentCentered sx={{ height: "80vh" }}>
                                <Loader label="Loading dashboard..." />
                            </LayoutFillParentCentered>
                        ) : activeTab === "document-engine" ? (
                            <DocEngineRequestsTable
                                rows={
                                    dashboardAnalytics?.docEngineRequests ?? []
                                }
                            />
                        ) : activeTab === "preference" ? (
                            <UserPreferencesTable
                                rows={dashboardAnalytics?.userPreferences ?? []}
                            />
                        ) : (
                            <div
                                style={{
                                    width: "80%",
                                    margin: "0 auto",
                                }}
                            >
                                <DraggableGrid
                                    nodes={nodes}
                                    options={{
                                        rowHeight:
                                            viewHeightToPixels(CELL_HEIGHT),
                                        compactType: "vertical",
                                        cols: {
                                            lg: 6,
                                            md: 6,
                                            sm: 4,
                                            xs: 2,
                                            xxs: 1,
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <LayoutFillParentCentered sx={{ height: "80vh" }}>
                        <ErrorMessage>
                            {isProdaUser
                                ? "There was an error loading the dashboard."
                                : "🛑 This page is only available to Proda users."}
                        </ErrorMessage>
                    </LayoutFillParentCentered>
                )}
            </Box>
        );
    };
    return ViewDashboard;
};
